import {
  Box,
  Divider,
  IconButton as MUIIconButton,
  Menu,
  MenuItem as MUIMenuItem,
  Tooltip,
  Typography,
  dividerClasses,
  ListItemIcon,
  listItemIconClasses,
  SvgIcon,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChangePasswordDialog } from '@/features/changePassword';
import {
  logout,
  selectEmail,
  selectIsAdmin,
  selectViewsRestrictions,
  ViewAccessType,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { Monitoring } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import {
  AvatarIcon,
  DatasourcesIcon,
  UserManagementIcon,
  WebLinksIcon,
} from '@/shared/ui';

const NO_EMAIL_PLACEHOLDER = 'Unknow user';

const IconButton = styled(MUIIconButton)(() => ({
  height: '100%',
  aspectRatio: '1 / 1',
}));
const MenuItem = styled(MUIMenuItem)(({ theme }) => ({
  fontSize: '14px',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  height: 36,

  [`+.${dividerClasses.root}`]: {
    margin: 0,
  },

  [`& .${listItemIconClasses.root}`]: {
    minWidth: 24,
    marginRight: theme.spacing(1),
  },
})) as typeof MUIMenuItem;

export function ProfileMenu() {
  const dispatch = useAppDispatch();
  const email = useAppSelector(selectEmail);
  const isAdmin = useAppSelector(selectIsAdmin);
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] =
    useState(false);

  const toggleMenu = (newValue: boolean) => () => {
    setMenuOpen(newValue);
  };
  const toggleChangePasswordOpen = (newValue: boolean) => () => {
    setChangePasswordDialogOpen(newValue);
  };
  const showReportDialog = () => {
    Monitoring.showReportDialog();
  };
  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <>
      <Tooltip title="Account settings">
        <IconButton
          ref={anchorRef}
          sx={{ mx: -1, height: 56, width: 56 }}
          onClick={toggleMenu(!menuOpen)}
        >
          <AvatarIcon sx={{ width: 40, height: 40, color: 'primary.main' }} />
        </IconButton>
      </Tooltip>
      <Menu
        MenuListProps={{
          disablePadding: true,
        }}
        slotProps={{
          paper: {
            sx: {
              width: 224,
              borderRadius: 2,
            },
          },
        }}
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={toggleMenu(false)}
        onClick={toggleMenu(false)}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            height: 184,
          }}
        >
          <AvatarIcon sx={{ width: 64, height: 64, color: 'primary.main' }} />
          <Typography
            sx={{
              fontSize: '0.75rem',
              lineHeight: '0.875rem',
              color: 'text.secondary',
              wordBreak: 'break-all',
              textAlign: 'center',
            }}
            title={email}
          >
            {email || NO_EMAIL_PLACEHOLDER}
          </Typography>
        </Box>
        {isAdmin && (
          <div>
            <Divider />
            <MenuItem component={Link} to={ROUTES.userManagement}>
              <ListItemIcon>
                <UserManagementIcon />
              </ListItemIcon>
              User management
            </MenuItem>
          </div>
        )}
        <Divider />
        {viewsRestrictions?.webLinks !== ViewAccessType.DENIED && (
          <MenuItem component={Link} to={ROUTES.webLinks}>
            <ListItemIcon>
              <WebLinksIcon />
            </ListItemIcon>
            Web links
          </MenuItem>
        )}
        {viewsRestrictions?.integrations !== ViewAccessType.DENIED && (
          <MenuItem component={Link} to={ROUTES.datasources}>
            <ListItemIcon>
              <DatasourcesIcon sx={{ color: '#666666' }} />
            </ListItemIcon>
            Integrations
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={toggleChangePasswordOpen(true)}>
          <ListItemIcon>
            <SvgIcon>
              <svg
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.9167 6.24935L14.8334 8.16602C14.9892 8.3187 15.1986 8.40423 15.4167 8.40423C15.6349 8.40423 15.8443 8.3187 16.0001 8.16602L17.7501 6.41602C17.9028 6.26024 17.9883 6.05081 17.9883 5.83268C17.9883 5.61455 17.9028 5.40512 17.7501 5.24935L15.8334 3.33268M17.5001 1.66602L9.50008 9.66602M10.8334 12.916C10.8334 15.4473 8.78139 17.4993 6.25008 17.4993C3.71878 17.4993 1.66675 15.4473 1.66675 12.916C1.66675 10.3847 3.71878 8.33268 6.25008 8.33268C8.78139 8.33268 10.8334 10.3847 10.8334 12.916Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SvgIcon>
          </ListItemIcon>
          Change password
        </MenuItem>
        <MenuItem onClick={showReportDialog}>
          <ListItemIcon>
            <SvgIcon>
              <svg
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.66667 14C9.57912 14.3174 9.42992 14.6144 9.22759 14.8741C9.02525 15.1338 8.77375 15.3511 8.48744 15.5136C8.20112 15.6761 7.88561 15.7806 7.5589 15.8212C7.23219 15.8617 6.90069 15.8375 6.58333 15.75C6.26597 15.6625 5.96896 15.5133 5.70926 15.3109C5.44956 15.1086 5.23226 14.8571 5.06975 14.5708C4.90725 14.2845 4.80273 13.9689 4.76217 13.6422C4.7216 13.3155 4.74579 12.984 4.83333 12.6667M2.5 9.16667L17.5 5V15L2.5 11.6667V9.16667Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SvgIcon>
          </ListItemIcon>
          Report feedback
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <SvgIcon>
              <svg
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5M13.3333 14.1667L17.5 10M17.5 10L13.3333 5.83333M17.5 10H7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SvgIcon>
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={toggleChangePasswordOpen(false)}
      />
    </>
  );
}
